import * as THREE from "three";
import { MATS } from "../materials.js";
import { makeRng } from "../builder.js";
import * as B from "./backspec.js";

/**
 * The back garden proper: the freshly-laid sod lawn, the curved side bed with
 * its feature planting, the conifer screen, and the back border.  The deck,
 * patio, shed and fences are other modules' work and are only avoided here.
 *
 * What this module honours:
 *   - Every shrub, conifer and tree crown is collider-free -- the cat pushes
 *     through planting, and a solid screen would seal the rear of the garden.
 *     Only the two tree trunks and the ankle-high paver edging collide, and
 *     the edging tops out at 0.10 m, under the cat's 0.23 m step height.
 *   - B.KEEP_CLEAR: no planting near the board gap at (B.LOT_MIN_X, -19.3) or
 *     the rear gate at (0.4, -30); ground decals may pass through, plants do
 *     not.
 *   - The lawn rectangle overlaps the patio's in the spec, so every sod roll
 *     and the soil underlay are clipped against the patio footprint rather
 *     than trusting draw order.
 *   - The boundary is B.plantLineX / B.insideBoundary and nothing else.  Not
 *     one x in this file is measured off the fence by hand, and that is the
 *     whole point: the screen used to be authored at +/-7.02 against a lot
 *     line at +/-7.25, spec.js later tightened the subdivision to +/-6.75,
 *     fences.js moved because it reads LOT_EDGES, and this module did not --
 *     so fifty-two crowns grew through the neighbours' fences by up to half a
 *     metre.  Every plant now asks the spec where the boards are and states
 *     the crown it has to keep on this side of them.  Move the lots again and
 *     the planting moves with them.
 */

// Decal stack, bottom to top, so nothing is ever coplanar: soil underlay tops
// at 0.024, mulch at 0.038, sod at 0.047.  The 23 mm the sod stands proud of
// the underlay is what makes the seams between rolls read as real joints.
const SOIL_Y = 0.004, SOIL_TH = 0.020;
const MULCH_Y = 0.012, MULCH_TH = 0.026;
const SOD_Y = 0.022, SOD_TH = 0.025;
const SEAM_GAP = 0.04;            // dark underlay showing between rolls

// The patio spec rectangle, padded a touch, as a hole to clip decals against.
const PATIO_HOLE = {
  x0: B.PATIO.minX - 0.04, x1: B.PATIO.maxX + 0.04,
  z0: B.PATIO.minZ - 0.04, z1: B.PATIO.maxZ + 0.04,
};

/** Rectangle minus rectangle, as up to four rectangles. */
function subtract(r, h) {
  if (r.x1 <= h.x0 || r.x0 >= h.x1 || r.z1 <= h.z0 || r.z0 >= h.z1) return [r];
  const out = [];
  if (r.x0 < h.x0) out.push({ x0: r.x0, x1: h.x0, z0: r.z0, z1: r.z1 });
  if (r.x1 > h.x1) out.push({ x0: h.x1, x1: r.x1, z0: r.z0, z1: r.z1 });
  const mx0 = Math.max(r.x0, h.x0), mx1 = Math.min(r.x1, h.x1);
  if (r.z0 < h.z0) out.push({ x0: mx0, x1: mx1, z0: r.z0, z1: h.z0 });
  if (r.z1 > h.z1) out.push({ x0: mx0, x1: mx1, z0: h.z1, z1: r.z1 });
  return out;
}

// ---- the bed curve ----------------------------------------------------------

/**
 * Lawn-side edge of the side bed.  The spec gives two depths -- a bulge near
 * the house tapering away -- so the curve is a cosine ease between them plus
 * a gentle mid-garden swell, which is what reads as "sweeping" rather than a
 * straight taper.  Everything left of the lawn keys off this one function:
 * the mulch strips, the paver edging, and where each sod row starts.
 *
 * Both depths are measured from B.SIDE_BED.minX, which is the boundary fence
 * -- so this curve moved with the fence when the lots were tightened, and the
 * depths were re-datumed to hold the near end of it still.  See SIDE_BED.
 */
function bedEdgeX(z) {
  const t = Math.min(1, Math.max(0,
    (B.SIDE_BED.maxZ - z) / (B.SIDE_BED.maxZ - B.SIDE_BED.minZ)));
  const ease = 0.5 - 0.5 * Math.cos(Math.PI * t);
  const depth = B.SIDE_BED.depthNear
    + (B.SIDE_BED.depthFar - B.SIDE_BED.depthNear) * ease
    + 0.30 * Math.sin(Math.PI * t) * (1 - 0.4 * t);
  return B.SIDE_BED.minX + depth;
}

// ---- sod --------------------------------------------------------------------

/** One roll's colour: mostly sod/sodSeam alternating on a checker so every
 *  joint sits between two different greens, with the odd roll from another
 *  pallet and one drying out -- the tells of a lawn laid last week. */
function rollMat(rng, parity) {
  const p = rng();
  if (p < 0.028) return MATS.lawnDry;
  if (p < 0.13) return MATS.lawn;
  return parity ? MATS.sodSeam : MATS.sod;
}

/**
 * The whole lawn as individual roll-sized slabs over a dark soil underlay.
 * Rows run across the garden and are laid from the far end so the one cut-down
 * row lands by the patio, like a real crew working away from the gate.  Odd
 * rows shift by half a roll for the brick-stagger in the photo.
 */
function sodField(b, rng) {
  const RW = B.LAWN.rollWidth, RL = B.LAWN.rollLength;

  for (const u of subtract({ x0: B.SIDE_BED.minX, x1: B.LAWN.maxX,
                             z0: B.LAWN.minZ, z1: B.LAWN.maxZ }, PATIO_HOLE)) {
    b.slab((u.x0 + u.x1) / 2, SOIL_Y, (u.z0 + u.z1) / 2,
      u.x1 - u.x0, u.z1 - u.z0, MATS.soil, { thickness: SOIL_TH, tile: 0.9 });
  }

  const rows = Math.ceil((B.LAWN.maxZ - B.LAWN.minZ) / RW);
  for (let r = 0; r < rows; r++) {
    const z0 = B.LAWN.minZ + r * RW;
    const z1 = Math.min(z0 + RW, B.LAWN.maxZ);
    if (z1 - z0 < 0.12) continue;

    // The bed curve eats into the row; start from its deepest point across
    // the row so no roll pokes past the edging into the mulch.
    const intrusion = Math.max(bedEdgeX(z0), bedEdgeX((z0 + z1) / 2), bedEdgeX(z1));
    const xL = Math.max(B.LAWN.minX, intrusion + 0.03);

    // Roll ends snap to a garden-wide grid (offset half a roll on odd rows)
    // so the stagger lines up row to row; end rolls are cut to fit.
    const cuts = [xL];
    const off = (r % 2) * RL / 2;
    for (let s = B.LAWN.minX + off; s < B.LAWN.maxX; s += RL) {
      if (s > xL + 0.3 && s < B.LAWN.maxX - 0.3) cuts.push(s);
    }
    cuts.push(B.LAWN.maxX);

    let c = r;    // running column index keeps the checker moving with the stagger
    for (let i = 0; i + 1 < cuts.length; i++) {
      for (const p of subtract({ x0: cuts[i], x1: cuts[i + 1], z0, z1 }, PATIO_HOLE)) {
        const w = p.x1 - p.x0 - SEAM_GAP, d = p.z1 - p.z0 - SEAM_GAP;
        if (w < 0.10 || d < 0.10) continue;
        b.slab((p.x0 + p.x1) / 2, SOD_Y, (p.z0 + p.z1) / 2, w, d,
          rollMat(rng, (c) & 1), { thickness: SOD_TH });
        c++;
      }
    }
  }
}

// ---- beds and edging ---------------------------------------------------------

/** Side-bed mulch in metre strips out to the curve, tucked under the pavers. */
function sideBedMulch(b) {
  for (let z1 = B.SIDE_BED.maxZ; z1 > B.SIDE_BED.minZ + 0.01; z1 -= 1.0) {
    const z0 = Math.max(z1 - 1.0, B.SIDE_BED.minZ);
    const zc = (z0 + z1) / 2;
    const x1 = bedEdgeX(zc) + 0.10;
    b.slab((B.SIDE_BED.minX + x1) / 2, MULCH_Y, zc,
      x1 - B.SIDE_BED.minX, z1 - z0, MATS.mulch, { thickness: MULCH_TH, tile: 0.8 });
  }
}

/** Tan pavers laid end to end along the curve, each yawed to the local
 *  tangent.  0.072 m tall on a 0.028 m bed: tops at 0.10 m, so they collide
 *  (they feel like kerbs underfoot) without ever blocking the step-over. */
function paverEdging(b, rng) {
  const d = 0.12;    // finite-difference half-step for the tangent
  for (let z = B.SIDE_BED.maxZ - 0.22; z > B.SIDE_BED.minZ + 0.12; z -= 0.36) {
    const x = bedEdgeX(z);
    const a = Math.atan2(bedEdgeX(z - d) - bedEdgeX(z + d), -2 * d)
      + (rng() - 0.5) * 0.06;
    const g = new THREE.BoxGeometry(0.18, 0.072, 0.34);
    g.rotateY(a);
    g.translate(x, 0.028 + 0.036, z);
    b.add(g, MATS.edgePaver, {
      tile: 0.35,
      collider: { x, y: 0.028, z, w: 0.34, h: 0.072, d: 0.34 },
    });
  }
}

/** Bare mulch along the fence bases: the back border either side of the
 *  shed's gravel pad, and a strip under the right-hand run of the screen.
 *
 *  The +X strips stop BED_INSET short of the boundary, the mirror of what
 *  SIDE_BED.minX does on the -X side.  They used to run out to a typed 7.3,
 *  which by the time the lots were tightened was mulch laid straight through
 *  the fence and out into lot 2's garden. */
function fenceMulch(b) {
  const shedL = B.SHED.x - B.SHED.width / 2 - B.SHED.padOversize - 0.05;
  const shedR = B.SHED.x + B.SHED.width / 2 + B.SHED.padOversize + 0.05;
  const mulchR = B.LOT_MAX_X - B.BED_INSET;   // +X edge of any bed, as minX is on -X
  const z0 = B.BACK_BED.minZ, z1 = -27.5;     // abuts the side-bed strips exactly
  b.slab((B.SIDE_BED.minX + shedL) / 2, MULCH_Y, (z0 + z1) / 2,
    shedL - B.SIDE_BED.minX, z1 - z0, MATS.mulch, { thickness: MULCH_TH, tile: 0.8 });
  b.slab((shedR + mulchR) / 2, MULCH_Y, (z0 + z1) / 2,
    mulchR - shedR, z1 - z0, MATS.mulch, { thickness: MULCH_TH, tile: 0.8 });
  // The strip the +X screen run stands in: 0.75 m of mulch inside the line,
  // which is wide enough for a cedar seated on it and its jitter either way.
  b.slab(mulchR - 0.375, MULCH_Y, (-27.5 + -20.5) / 2, 0.75, 7.0, MATS.mulch,
    { thickness: MULCH_TH, tile: 0.8 });
}

// ---- planting ----------------------------------------------------------------

// One cedar's size, drawn before it is placed, because where it may stand
// depends on how wide it came out: the runs seat each tree on the clearance
// line for ITS crown rather than pushing the whole screen back to suit the
// biggest tree the generator can produce.
const CEDAR_H = { min: 3.1, span: 1.5 };
const cedarR = (h) => 0.40 + h * 0.045;
const CEDAR_MAX_R = cedarR(CEDAR_H.min + CEDAR_H.span);   // 0.607, the worst case
const cedarSize = (rng) => {
  const h = CEDAR_H.min + rng() * CEDAR_H.span;
  return { h, r: cedarR(h) };
};

/** One emerald cedar: two stacked 6-segment cones, 24 triangles the pair, so
 *  the whole screen stays cheap.  Cones carry no collider at all. */
function cedar(b, rng, x, z, s) {
  const mat = rng() < 0.3 ? MATS.coniferLit : MATS.conifer;
  b.cone(x, 0.04, z, s.r, s.h * 0.66, mat, { segments: 6 });
  b.cone(x, s.h * 0.40, z, s.r * 0.72, s.h * 0.60, mat, { segments: 6 });
}

// Half the across-the-run scatter.  It stays symmetric -- a run of cedars all
// breathing in and out about a line is what makes a planted screen read as
// planted rather than fenced -- so the LINE is drawn for a crown plus this,
// and the jitter then spends it in either direction.  Pulling the jitter
// one-sided would have been the cheaper fix and would have shifted the whole
// screen 8 cm off the boards for no reason.
const SCREEN_JIT_X = 0.08;

/**
 * One side run of the screen, laid ON the clearance line for the -X or +X
 * boundary.  A boundary screen is supposed to hug the boards, so it is not
 * clamped back off an authored x: the line IS the authored position, and it
 * comes from B.plantLineX, which comes from LOT_EDGES.
 */
function screenSide(b, rng, sign, z0, z1) {
  for (let z = z0; z <= z1; z += 0.76) {
    const s = cedarSize(rng);
    const jx = B.plantLineX(sign, s.r + SCREEN_JIT_X)
      + (rng() - 0.5) * 2 * SCREEN_JIT_X;
    const jz = z + (rng() - 0.5) * 0.2;
    if (!B.clearOf(jx, jz, 0.7)) continue;
    cedar(b, rng, jx, jz, s);
  }
}

/** The screen: a packed back row skipping the shed and the rear gate, plus
 *  shorter runs part-way down both sides.  All of it walk-through. */
function coniferScreen(b, rng) {
  const shedHalf = B.SHED.width / 2 + 0.85;
  // The back row ends where the widest cedar plus its scatter still clears
  // the side fences, which is also where the side runs stand -- so the two
  // corners close instead of leaving a notch or, as before, crossing.
  const x0 = B.plantLineX(-1, CEDAR_MAX_R + 0.11);
  const x1 = B.plantLineX(1, CEDAR_MAX_R + 0.11);
  for (let x = x0; x <= x1 + 1e-9; x += 0.74) {
    const s = cedarSize(rng);
    // Belt and braces: the row's span already allows for the scatter, and
    // insideBoundary guarantees it for this tree's actual crown.
    const jx = B.insideBoundary(x + (rng() - 0.5) * 0.22, s.r);
    const jz = -29.05 + (rng() - 0.5) * 0.25;
    if (Math.abs(jx - B.SHED.x) < shedHalf) continue;   // hidden behind the shed anyway
    if (!B.clearOf(jx, jz, 0.7)) continue;              // keeps crowns off the rear gate
    cedar(b, rng, jx, jz, s);
  }
  screenSide(b, rng, -1, -28.55, -23.35);   // stops well shy of the board gap at z -19.3
  screenSide(b, rng, 1, -28.55, -21.0);
}

// What each specimen reaches sideways from the x it is planted at -- which is
// never just its radius, because every one of these is a cluster with the
// satellites offset.  Each is the widest the generator below can draw, and
// each is the number that specimen hands to B.insideBoundary: a shrub planted
// dead on the line is fine, a shrub whose third blob is 40% of a radius past
// the line is a shrub in next door's garden.  Keep these next to the geometry
// they describe; if a blob moves, the reach moves in the same edit.
const SHRUB_REACH = 1.40;   // times r: offset 0.65 r plus that lobe's 0.75 r
const BOXWOOD_REACH = 0.89; // metres: satellite at -0.45 with radius 0.44
const MAPLE_REACH = 1.17;   // metres: blob at -0.62 with radius up to 0.55
const FRUIT_REACH = 1.00;   // metres: blob at -0.50 with radius 0.50

/** A loose shrub: two or three overlapping squashed blobs. Never a collider. */
function shrub(b, rng, x0, z, r, mat) {
  const x = B.insideBoundary(x0, r * SHRUB_REACH);
  b.sphere(x, 0.03, z, r, mat,
    { squash: 0.72 + rng() * 0.14, segments: 6, rings: 4 });
  const n = r > 0.42 ? 2 : 1;
  for (let i = 0; i < n; i++) {
    const a = rng() * Math.PI * 2, off = r * (0.4 + rng() * 0.25);
    b.sphere(x + Math.cos(a) * off, 0.03, z + Math.sin(a) * off,
      r * (0.55 + rng() * 0.2), mat,
      { squash: 0.68 + rng() * 0.16, segments: 6, rings: 4 });
  }
}

/** The big boxwood -- the brightest, roundest mass in the photo, so it gets
 *  the roundest geometry in the garden: one fat sphere with two satellites. */
function boxwood(b, rng, x0, z) {
  const x = B.insideBoundary(x0, BOXWOOD_REACH);
  b.sphere(x, 0.04, z, 0.82, MATS.boxwood, { squash: 0.86, segments: 9, rings: 6 });
  b.sphere(x - 0.45, 0.03, z + 0.35, 0.44, MATS.boxwood,
    { squash: 0.8, segments: 7, rings: 5 });
  b.sphere(x + 0.4, 0.03, z - 0.3, 0.38 + rng() * 0.05, MATS.boxwood,
    { squash: 0.78, segments: 7, rings: 5 });
}

/** Japanese maple: tapered trunk (collides, to just under the crown) with a
 *  broad low mound of deep red blobs (does not). */
function japaneseMaple(b, rng, x0, z) {
  const x = B.insideBoundary(x0, MAPLE_REACH);
  b.post(x, 0, z, 0.09, 1.25, MATS.barkDark, { topRadius: 0.05, segments: 8 });
  const blobs = [
    [0, 1.05, 0, 0.78], [-0.62, 1.2, 0.3, 0.5],
    [0.55, 1.3, -0.25, 0.52], [0.1, 1.62, 0.28, 0.55],
  ];
  for (const [dx, y, dz, r] of blobs) {
    b.sphere(x + dx, y, z + dz, r + rng() * 0.05, MATS.acerRed,
      { squash: 0.6 + rng() * 0.08, segments: 7, rings: 5 });
  }
}

/** The little fruit tree in the far-left corner: bark trunk, leafy crown with
 *  a warm blob for depth, and a handful of red fruit dotted on the shell.
 *  Returns where it actually went, for the mark. */
function fruitTree(b, rng, x0, z) {
  const x = B.insideBoundary(x0, FRUIT_REACH);
  b.post(x, 0, z, 0.10, 1.5, MATS.bark, { topRadius: 0.065, segments: 8 });
  b.sphere(x, 1.35, z, 0.92, MATS.leaf, { squash: 0.88, segments: 8, rings: 6 });
  b.sphere(x - 0.5, 1.75, z + 0.35, 0.5, MATS.leaf,
    { squash: 0.85, segments: 6, rings: 4 });
  b.sphere(x + 0.45, 1.9, z - 0.3, 0.48, MATS.leafWarm,
    { squash: 0.85, segments: 6, rings: 4 });
  for (let i = 0; i < 6; i++) {                       // apples on the crown shell
    const a = rng() * Math.PI * 2, e = rng() * Math.PI * 0.4;
    b.sphere(x + Math.cos(a) * Math.cos(e) * 0.88, 1.7 + Math.sin(e) * 0.75,
      z + Math.sin(a) * Math.cos(e) * 0.88, 0.055, MATS.flowerWarm,
      { segments: 5, rings: 3 });
  }
  return x;
}

// ---- build -------------------------------------------------------------------

export function build(b) {
  const rng = makeRng(0xB0C5C0DE);

  sodField(b, rng);
  sideBedMulch(b);
  paverEdging(b, rng);
  fenceMulch(b);
  coniferScreen(b, rng);

  // The side bed's cast, house end to far end, as in the photo.  The boxwood
  // and its neighbours sit north of z -18.2 so nothing green encroaches on
  // the board gap at (B.LOT_MIN_X, -19.3).
  //
  // The x here is where the photograph puts each one along the bed, and every
  // one of them is planted through insideBoundary, so a specimen the boundary
  // has caught up with is pulled back by exactly its own crown and no more --
  // the big-crowned ones (the maple, the fruit tree) end up further off the
  // boards than the little shrubs between them, which is what a bed planted
  // against a fence looks like anyway.  These were all authored against the
  // old +/-7.5 lot line, so today they all clamp; leaving the authored spots
  // in means they come back out again if the subdivision ever loosens.
  japaneseMaple(b, rng, -6.15, -13.6);
  const coneX = B.insideBoundary(-6.55, 0.42);   // 0.42: the lower, wider cone
  b.cone(coneX, 0.03, -15.7, 0.42, 1.5, MATS.coniferLit, { segments: 7 });
  b.cone(coneX, 0.95, -15.7, 0.3, 1.35, MATS.coniferLit, { segments: 7 });
  boxwood(b, rng, -6.0, -17.2);
  shrub(b, rng, -6.7, -21.1, 0.34, MATS.hedge);
  shrub(b, rng, -6.45, -23.1, 0.42, MATS.leafDark);
  shrub(b, rng, -6.8, -25.0, 0.3, MATS.boxwood);
  const fruitX = fruitTree(b, rng, -6.25, -26.6);

  // A few shrubs at the foot of the screen, either side of the shed.
  shrub(b, rng, -5.6, -27.9, 0.45, MATS.hedge);
  shrub(b, rng, -4.2, -28.1, 0.36, MATS.leafDark);
  shrub(b, rng, 3.3, -27.9, 0.42, MATS.hedge);
  shrub(b, rng, 5.2, -28.05, 0.38, MATS.leafDark);
  shrub(b, rng, 6.3, -27.8, 0.32, MATS.hedge);

  b.mark("lawn centre", 0, 0.05, -22);
  b.mark("fruit tree", fruitX, 0, -26.6);   // where it went, not where it was asked for
}
